import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import { useMarkAsSold } from '@/hooks/useTransactions';
import { UserItem } from '@/types/userItem';
import { toast } from '@/lib/toast';
import { Loader2 } from 'lucide-react';
import { JobProgress } from '@/components/ui/JobProgress';

interface BulkMarkAsSoldSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: UserItem[];
  onSuccess?: () => void;
}

/**
 * Stripped-down bulk Mark as Sold flow for vendors closing out a card show.
 * No buyer linking, no review prompt, no photo. Just price + date + channel.
 */
export function BulkMarkAsSoldSheet({ open, onOpenChange, selectedItems, onSuccess }: BulkMarkAsSoldSheetProps) {
  const markAsSold = useMarkAsSold();
  const [useAskingPrice, setUseAskingPrice] = useState(true);
  const [flatPrice, setFlatPrice] = useState('');
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [channel, setChannel] = useState<'cash' | 'other'>('cash');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });

  // Reset on open
  useEffect(() => {
    if (open) {
      setUseAskingPrice(true);
      setFlatPrice('');
      setSaleDate(new Date().toISOString().slice(0, 10));
      setChannel('cash');
    }
  }, [open]);

  const itemsWithoutPrice = useMemo(
    () => selectedItems.filter(i => !i.asking_price_cents || i.asking_price_cents <= 0),
    [selectedItems]
  );

  const flatPriceCents = (() => {
    const n = parseFloat(flatPrice);
    if (!isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  })();

  const eligibleCount = useAskingPrice
    ? selectedItems.length - itemsWithoutPrice.length
    : flatPriceCents > 0 ? selectedItems.length : 0;

  const canSubmit = !submitting && eligibleCount > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);

    const saleDateObj = new Date(saleDate + 'T12:00:00');
    const targets = useAskingPrice
      ? selectedItems.filter(i => (i.asking_price_cents ?? 0) > 0)
      : selectedItems;

    let success = 0;
    let failed = 0;
    setProgress({ done: 0, total: targets.length, failed: 0 });

    for (const item of targets) {
      const priceCents = useAskingPrice ? (item.asking_price_cents ?? 0) : flatPriceCents;
      try {
        await markAsSold.mutateAsync({
          itemId: item.id,
          salePriceCents: priceCents,
          paymentMethod: channel,
          saleDate: saleDateObj,
        });
        success++;
      } catch (e) {
        console.error('Bulk mark sold failed for', item.id, e);
        failed++;
      }
      setProgress({ done: success + failed, total: targets.length, failed });
    }

    setSubmitting(false);

    if (success > 0) {
      const parts = [`${success} item${success !== 1 ? 's' : ''} marked sold`];
      if (failed > 0) parts.push(`${failed} failed`);
      if (useAskingPrice && itemsWithoutPrice.length > 0) {
        parts.push(`${itemsWithoutPrice.length} skipped (no price)`);
      }
      if (failed > 0) toast.error(parts.join(' · '));
      onOpenChange(false);
      onSuccess?.();
    } else {
      toast.error('Failed to mark items sold');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md bg-chrome border-white/[0.08]">
        <DialogHeader>
          <DialogTitle>
            Mark {selectedItems.length} sold
          </DialogTitle>
          <DialogDescription className="sr-only">
            Mark selected items as sold.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Use asking price toggle */}
          <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 h-12">
            <Label htmlFor="use-asking" className="text-sm font-medium cursor-pointer">
              Use each item's price
            </Label>
            <Switch id="use-asking" checked={useAskingPrice} onCheckedChange={setUseAskingPrice} />
          </div>

          {/* Flat price input (when toggle off) */}
          {!useAskingPrice && (
            <div className="space-y-1.5">
              <Label htmlFor="flat-price" className="text-sm">Sale price (each)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  id="flat-price"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  {...PRICE_INPUT_PROPS}
                  value={flatPrice}
                  onChange={(e) => setFlatPrice(e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="0.00"
                  className="pl-7 bg-black/20 border-white/10"

                />
              </div>
            </div>
          )}

          {/* No-price warning */}
          {useAskingPrice && itemsWithoutPrice.length > 0 && (
            <div className="text-xs text-warning/90 px-1">
              {itemsWithoutPrice.length} item{itemsWithoutPrice.length !== 1 ? 's have' : ' has'} no price — they'll be skipped.
            </div>
          )}

          {/* Sale date */}
          <div className="space-y-1.5">
            <Label htmlFor="sale-date" className="text-sm">Sale date</Label>
            <Input
              id="sale-date"
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              className="bg-black/20 border-white/10"
            />
          </div>

          {submitting && (
            <JobProgress label="Marking sold" done={progress.done} total={progress.total} failed={progress.failed} isRunning />
          )}

          <p className="text-[11px] text-muted-foreground/70 px-1">
            Sold items move to Sold and are pulled from eBay automatically. Tap any item later to add trade or cost details.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="bg-white/[0.05] border-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-active-gold/95 hover:bg-active-gold text-black font-semibold border-0"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {submitting
              ? `Marking ${progress.done} of ${progress.total}…`
              : `Mark ${eligibleCount || selectedItems.length} sold`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
